// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tippy, { TippyProps } from "@tippyjs/react";
import { Instance as TippyInstance, sticky } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import { TippPopupProps } from "@hardwoods/App/Components/XngTooltip/Interface";
import { xngTooltipStyles } from "@hardwoods/App/Components/XngTooltip/Style";
import { xCommonUpdate } from "@hardwoods/App/Handlers/XNG/Common";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getCommon, getHeader } from "@hardwoods/App/Store/Selectors/CommonState";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

const mapDispatchToProps = {
    xCommonUpdate,
};

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: getHeader(state).isSticky,
    isMobile: getCommon(state).isMobile,
});

type Props = TippyProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    TippPopupProps;

export const defaultTippyTooltipProps: TippyProps = {
    placement: "top",
    interactive: false,
    trigger: "click",
    offset: [0, 8],
    animation: "shift-away",
    sticky: "reference",
    plugins: [sticky],
    arrow: true,
    inertia: false,
};

const XngTooltip: FC<Props> = ({
    onPopupVisibilityUpdate: onPopupVisibilityUpdateProps,
    enableMobileMode,
    updateTriggerOptionsOnMobile,
    isSticky,
    isMobile,
    children,
    withCloseButton,
    xCommonUpdate,
    extendedStyles,
    ...otherProps
}) => {
    const {
        onCreate: onCreateProps,
        content: tippyContent,
        onHide: onHideProps,
        onShow: onShowProps,
        ...tippyProps
    } = otherProps;
    const [tippyInstance, setTippyInstance] = useState<TippyInstance>();
    const [styles] = useState(() => mergeWithCss(xngTooltipStyles, extendedStyles));

    const applyCssFiles = () => {
        /* eslint-disable global-require */
        require("tippy.js/dist/tippy.css");
        require("tippy.js/animations/shift-away.css");
        /* eslint-enable global-require */
    };

    useEffect(() => {
        applyCssFiles();
    }, []);

    const contentWrapper = <StyledWrapper className="content-wrapper">{tippyContent}</StyledWrapper>;

    const onHide = (instance: TippyInstance) => {
        onPopupVisibilityUpdateProps && onPopupVisibilityUpdateProps(false);
        onHideProps && onHideProps(instance);
    };

    const onShow = (instance: TippyInstance) => {
        onPopupVisibilityUpdateProps && onPopupVisibilityUpdateProps(true);
        onShowProps && onShowProps(instance);
    };

    const mainProps = {
        enableMobileMode,
    };

    return (
        <StyledWrapper {...styles.main} {...mainProps} data-test-selector="xng-tooltip">
            <Tippy
                onCreate={instance => {
                    setTippyInstance(instance);
                    instance.popper.className = "tippy-tooltip";
                    onCreateProps && onCreateProps(instance);
                }}
                onHide={onHide}
                onShow={onShow}
                content={contentWrapper}
                {...tippyProps}
            >
                <span className="xng-tooltip-content-wrapper" style={styles.elementWrapper}>
                    {children}
                </span>
            </Tippy>
        </StyledWrapper>
    );
};

XngTooltip.defaultProps = {
    ...(defaultTippyTooltipProps as any),
    withCloseButton: false,
    enableMobileMode: true,
    updateTriggerOptionsOnMobile: false,
    delay: 0,
    duration: 0,
    maxWidth: "250px",
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(XngTooltip));
